import { CurrencyPipe } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { SubTask, Task, TaskTypeAction} from '@k12/models';

interface WageEarner {
  householdMemberName?: string | null;
  amountEarned?: number | null;
  irsW21099Issued?: boolean | null;
  employerName?: string | null;
}

interface VerificationOfNonFilingFormData {
  wageEarners?: WageEarner[];
  certified?: boolean | null;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-verification-of-non-filing-form',
  standalone: true,
  imports: [
    CurrencyPipe,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatIconModule,
    MatInputModule,
    MatRadioModule
  ],
  templateUrl: './verification-of-non-filing-form.component.html',
  styleUrl: './verification-of-non-filing-form.component.scss'
})
export class VerificationOfNonFilingFormComponent implements OnInit {
  @Input() task!: Task | null;
  @Input() taskAction!: SubTask | null;
  @Input() taskTypeAction!: TaskTypeAction | null;
  @Input() isReadOnly: boolean = false;
  @Output() submitFormData = new EventEmitter<string>();

  private readonly fb = inject(FormBuilder);

  protected form!: FormGroup;
  protected context: VerificationOfNonFilingFormData = {};

  public ngOnInit(): void {
    const raw = this.taskAction?.formData ? this.safeParseJson(this.taskAction.formData) : {};
    this.context = this.normalizeKeys(raw) as VerificationOfNonFilingFormData;

    this.form = this.fb.group({
      wageEarners: this.fb.array([]),
      certified: [this.context.certified ?? false, Validators.requiredTrue]
    });

    const wageEarners = this.context.wageEarners?.length ? this.context.wageEarners : [];
    wageEarners.forEach((earner) => this.wageEarners.push(this.createWageEarnerGroup(earner)));

    if (this.isReadOnly) {
      this.form.disable({ emitEvent: false });
    }
  }

  protected get wageEarners(): FormArray {
    return this.form.get('wageEarners') as FormArray;
  }

  protected addWageEarner(): void {
    this.wageEarners.push(this.createWageEarnerGroup());
    this.form.markAsDirty();
  }

  protected removeWageEarner(index: number): void {
    this.wageEarners.removeAt(index);
    this.form.markAsDirty();
  }

  protected isFormReady(): boolean {
    return this.form.valid && this.form.dirty && this.wageEarners.length > 0;
  }

  protected onBack(): void {
    globalThis.history.back();
  }

  protected submitForm(): void {
    if (!this.isFormReady()) return;

    this.submitFormData.emit(
      JSON.stringify({
        ...this.context,
        ...this.form.getRawValue()
      })
    );
  }

  private createWageEarnerGroup(earner: WageEarner = {}): FormGroup {
    return this.fb.group({
      householdMemberName: [earner.householdMemberName ?? '', Validators.required],
      amountEarned: [earner.amountEarned ?? null, [Validators.required, Validators.min(0)]],
      irsW21099Issued: [earner.irsW21099Issued ?? null, Validators.required],
      employerName: [earner.employerName ?? '', Validators.required]
    });
  }

  private safeParseJson(raw: string): Record<string, unknown> {
    try {
      const parsed = JSON.parse(raw) as unknown;
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  }

  private normalizeKeys(raw: Record<string, unknown>): Record<string, unknown> {
    return Object.fromEntries(
      Object.entries(raw).map(([key, value]) => [key.charAt(0).toLowerCase() + key.slice(1), value])
    );
  }
}
